import { useState, useEffect } from 'react';
import { nanoid } from 'nanoid';
import io, { Socket } from 'socket.io-client';
import {
  AuthRequest,
  AuthResponse,
  AgentMessagePayload,
  ProtocolWrapper,
  InboundData,
  GeneralMessageHandler,
  AgentMessageHandler,
} from '@/types/protocol';

import { useStudioStore } from '@/store/useStudioStore';
import { useChatStore } from '@/store/useChatStore';

/** 常量配置 */
const SOCKET_PATH = '/ws';
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || undefined;

/**
 * Socket 全局状态存储接口
 */
interface SocketStore {
  /** Socket.IO 原始引用 */
  socketRef: Socket | null;
  /** 当前生效的 Socket 实例（用于触发 React 更新） */
  socketInstance: Socket | null;
  /** 是否已连接并完成认证 */
  isConnected: boolean;
  /** 详细的连接状态 */
  connectionState: 'connecting' | 'connected' | 'disconnected';
  /** 最近一次业务认证使用的会话，用于 Socket.IO 自动重连后恢复订阅 */
  lastAuthSessionId: string | null | undefined;
  /** 普通消息监听器集合 */
  messageListeners: Set<GeneralMessageHandler>;
  /** AI 智能体专用消息监听器集合 */
  agentMessageListeners: Set<AgentMessageHandler>;
  /** 状态变更监听器（内部用于同步 Hook） */
  stateListeners: Set<(state: Snapshot) => void>;
}

/**
 * 内部单例状态机
 */
const socketStore: SocketStore = {
  socketRef: null,
  socketInstance: null,
  isConnected: false,
  connectionState: 'disconnected',
  lastAuthSessionId: undefined,
  messageListeners: new Set(),
  agentMessageListeners: new Set(),
  stateListeners: new Set(),
};

/**
 * 状态快照接口，用于 React 状态同步
 */
interface Snapshot {
  socketInstance: Socket | null;
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected';
}

/** 获取当前状态快照 */
const getSnapshot = (): Snapshot => ({
  socketInstance: socketStore.socketInstance,
  isConnected: socketStore.isConnected,
  connectionState: socketStore.connectionState,
});

/** 通知所有状态订阅者 */
const notifyState = () => {
  const snapshot = getSnapshot();
  socketStore.stateListeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.error('Socket state listener error:', error);
    }
  });
};

/** 更新全局状态并广播 */
const updateState = (patch: Partial<SocketStore>) => {
  Object.assign(socketStore, patch);
  notifyState();
};

/**
 * 确保 Socket 连接存在 (单例初始化与生命周期管理)
 */
export const ensureSocket = () => {
  if (socketStore.socketRef) {
    return socketStore.socketRef;
  }

  const socket = io(SOCKET_URL as string, {
    path: SOCKET_PATH,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
    autoConnect: true,
  });

  socketStore.socketRef = socket;
  updateState({ connectionState: 'connecting' });

  // 处理连接成功事件
  socket.on('connect', () => {
    console.log('[Socket.IO] 基础连接成功');
    updateState({
      connectionState: 'connected',
      socketInstance: socket,
    });

    if (socketStore.lastAuthSessionId !== undefined) {
      void socketActions.auth(socketStore.lastAuthSessionId).catch((error) => {
        console.error('[Socket.IO] Re-authentication after reconnect failed:', error);
      });
    }
  });

  socket.on('disconnect', () => {
    updateState({
      isConnected: false,
      socketInstance: null,
      connectionState: 'disconnected',
    });
  });

  socket.io.on('reconnect_attempt', () => {
    updateState({ connectionState: 'connecting' });
  });

  socket.on('connect_error', (error) => {
    console.error('Socket.IO connection error:', error);
    updateState({
      isConnected: false,
      socketInstance: null,
      connectionState: 'disconnected',
    });
  });

  // 监听 AI 专用事件
  socket.on('agentMessage', (payload: ProtocolWrapper<InboundData>) => {
    socketStore.agentMessageListeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error('Agent socket listener error:', error);
      }
    });
  });

  // 监听所有事件，用于扩展支持
  socket.onAny((eventName, ...args) => {
    if (eventName === 'agentMessage') return;
    socketStore.messageListeners.forEach((listener) => {
      try {
        listener({ eventName, data: args[0] });
      } catch (error) {
        console.error('Socket listener error:', error);
      }
    });
  });

  return socket;
};

/**
 * 手动关闭 Socket 连接
 */
export const teardownSocket = () => {
  const socket = socketStore.socketRef;
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }
  socketStore.socketRef = null;
  socketStore.socketInstance = null;
  socketStore.isConnected = false;
  socketStore.connectionState = 'disconnected';
  socketStore.lastAuthSessionId = undefined;
  socketStore.agentMessageListeners.clear();
  notifyState();
};

/**
 * SocketActions 接口定义
 */
export interface SocketActions {
  connect(timeout?: number): Promise<SocketActions>;
  auth(sessionId?: string | null): Promise<SocketActions>;
  waitForAuthenticated(timeout?: number): Promise<SocketActions>;
  sendAgentMessage(
    payload: AgentMessagePayload,
    options: { projectId: string; sessionId: string | null },
    ack?: (res: unknown) => void,
  ): SocketActions;
}

/**
 * socketActions - 导出的链式操作集合 (Fluent API)
 */
export const socketActions: SocketActions = {
  /**
   * 1. 确保基础连接已建立 (异步)
   */
  async connect(timeout = 10000): Promise<SocketActions> {
    ensureSocket();
    const socket = socketStore.socketRef;
    if (socket?.connected) return this;

    await new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        if (socketStore.socketRef?.connected) {
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          console.warn('[Socket] Connection timeout');
          resolve(false);
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });

    return this;
  },

  /**
   * 2. 发起业务认证 (异步)
   */
  async auth(sessionId?: string | null): Promise<SocketActions> {
    // 确保已建立基础连接
    await this.connect();

    const socket = socketStore.socketRef;
    if (!socket) throw new Error('Socket not initialized');

    const token = getAuthToken();
    if (!token) throw new Error('Auth token missing');

    const finalSessionId = sessionId || useStudioStore.getState().sessionId;
    socketStore.lastAuthSessionId = finalSessionId;
    const authReq: AuthRequest = { token, sessionId: finalSessionId };

    console.log('[Socket.IO] 正在发起业务层认证...', authReq);

    const success = await new Promise<boolean>((resolve) => {
      socket.emit('auth', authReq, (response: AuthResponse) => {
        console.log('[Socket.IO] 认证响应:', response);
        if (response && response.status === 'success') {
          updateState({ isConnected: true });
          resolve(true);
        } else {
          const finalMessage = response?.message || '未知错误';
          console.error('[Socket.IO] 认证失败:', finalMessage);
          useChatStore.getState().setBuildError(true, finalMessage);
          updateState({ isConnected: false });
          resolve(false);
        }
      });
    });

    if (!success) throw new Error('Socket authentication failed');
    return this;
  },

  /**
   * 等待业务认证完成 (异步)
   */
  async waitForAuthenticated(timeout = 10000): Promise<SocketActions> {
    if (socketStore.isConnected) return this;

    await new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        if (socketStore.isConnected) {
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          console.warn('[Socket] Auth timeout');
          resolve(false);
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });

    return this;
  },

  /**
   * 发送 AI 智能体消息
   */
  sendAgentMessage(
    payload: AgentMessagePayload,
    options: { projectId: string; sessionId: string | null },
    ack?: (res: unknown) => void,
  ): SocketActions {
    if (!socketStore.socketRef || !socketStore.isConnected) {
      console.warn('Socket not connected or not authenticated');
      return this;
    }

    const message: ProtocolWrapper<AgentMessagePayload> = {
      projectId: options.projectId,
      sessionId: options.sessionId,
      data: payload,
    };

    socketStore.socketRef.emit('agentMessage', message, ack);
    return this;
  },
};

/**
 * useWebSocket Hook
 */
export function useWebSocket() {
  const [snapshot, setSnapshot] = useState(() => getSnapshot());

  useEffect(() => {
    // 仅监听状态变更，不再自动触发 ensureSocket()
    socketStore.stateListeners.add(setSnapshot);
    return () => {
      socketStore.stateListeners.delete(setSnapshot);
    };
  }, []);

  return {
    socket: snapshot.socketInstance,
    isConnected: snapshot.isConnected,
    connectionState: snapshot.connectionState,
    actions: socketActions,
    sendAgentMessage: (
      payload: AgentMessagePayload,
      options: { projectId: string; sessionId: string | null },
      ack?: (res: unknown) => void,
    ) => {
      socketActions.sendAgentMessage(payload, options, ack);
    },
    onAgentMessage: (handler: AgentMessageHandler) => {
      if (typeof handler !== 'function') return () => {};
      socketStore.agentMessageListeners.add(handler);
      return () => {
        socketStore.agentMessageListeners.delete(handler);
      };
    },
  };
}

function getAuthToken() {
  return localStorage.getItem('token') || `dev-${nanoid()}`;
}
